export const nf = new Intl.NumberFormat("fr-FR");

export const cf = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
});

export const FORMULE_OPTIONS = [
  "Présence Free",
  "Présence",
  "Présence Pro",
  "Présence Ultim",
];

export const REGLEMENT_STATUS_OPTIONS = [
  "Réglé",
  "En attente",
  "Non réglé",
  "Annulé",
  "Remboursé",
];

export const PAYMENT_METHOD_OPTIONS = [
  "Virement bancaire",
  "Stripe",
  "Chèque",
  "Espèces",
  "Autre",
];

export const PAYMENT_STATUS_OPTIONS = [
  {
    value: "",
    label: "Aucun statut Stripe",
  },
  {
    value: "paid",
    label: "paid",
  },
  {
    value: "pending",
    label: "pending",
  },
  {
    value: "failed",
    label: "failed",
  },
  {
    value: "refunded",
    label: "refunded",
  },
  {
    value: "canceled",
    label: "canceled",
  },
];

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export function getReglementUser(reglement) {
  return (
    reglement?.User ||
    reglement?.user ||
    reglement?.Users ||
    {}
  );
}

export function getUserFullName(user) {
  const fullName = `${user?.prenom || ""} ${user?.nom || ""}`.trim();

  return fullName || user?.email || "Utilisateur inconnu";
}

export function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function toDateTimeLocal(value = new Date()) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const timezoneOffset = date.getTimezoneOffset() * 60_000;
  const localDate = new Date(date.getTime() - timezoneOffset);

  return localDate.toISOString().slice(0, 16);
}

export function dateTimeLocalToIso(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString();
}

export function getReglementStatusTone(value) {
  const normalizedValue = normalizeText(value);

  // Test exact : "Non réglé" ne doit surtout pas être vert.
  if (normalizedValue === "regle") return "emerald";

  if (
    normalizedValue === "en attente" ||
    normalizedValue === "non regle"
  ) {
    return "amber";
  }

  if (normalizedValue === "rembourse") return "blue";
  if (normalizedValue === "annule") return "rose";

  return "slate";
}

export function getPaymentStatusTone(value) {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === "paid") return "emerald";
  if (normalizedValue === "pending") return "amber";
  if (normalizedValue === "refunded") return "blue";

  if (
    normalizedValue === "failed" ||
    normalizedValue === "canceled"
  ) {
    return "rose";
  }

  return "slate";
}

export function getApiErrorMessage(
  error,
  fallback = "Une erreur est survenue.",
) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export function isPaidReglement(reglement) {
  const statut = normalizeText(reglement?.statut_reglement);
  const paymentStatus = normalizeText(reglement?.payment_status);

  return statut === "regle" || paymentStatus === "paid";
}

export function addOneYear(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  const next = new Date(date);
  next.setFullYear(next.getFullYear() + 1);
  return next;
}

export function differenceInDays(fromDate, toDate) {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((toDate - fromDate) / msPerDay);
}

export function getSubscriptionStatus(reglement, isReference = true) {
  if (!reglement?.date_achat) {
    return {
      label: "Inconnue",
      tone: "slate",
      progress: 0,
      daysLeft: null,
      expiryDate: null,
      isExpired: false,
      isSoon: false,
      isReference: false,
    };
  }

  if (!isReference) {
    return {
      label: "Historique",
      tone: "slate",
      progress: 0,
      daysLeft: null,
      expiryDate: addOneYear(reglement.date_achat),
      isExpired: false,
      isSoon: false,
      isReference: false,
    };
  }

  const startDate = new Date(reglement.date_achat);
  const expiryDate = addOneYear(reglement.date_achat);

  if (
    Number.isNaN(startDate.getTime()) ||
    !expiryDate ||
    Number.isNaN(expiryDate.getTime())
  ) {
    return {
      label: "Inconnue",
      tone: "slate",
      progress: 0,
      daysLeft: null,
      expiryDate: null,
      isExpired: false,
      isSoon: false,
      isReference: true,
    };
  }

  const now = new Date();
  const totalDuration = expiryDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();

  let progress = Math.round((elapsed / totalDuration) * 100);
  progress = Math.max(0, Math.min(100, progress));

  const daysLeft = differenceInDays(now, expiryDate);

  if (daysLeft < 0) {
    return {
      label: "Expiré",
      tone: "rose",
      progress: 100,
      daysLeft,
      expiryDate,
      isExpired: true,
      isSoon: false,
      isReference: true,
    };
  }

  if (daysLeft <= 30) {
    return {
      label: "À relancer",
      tone: "amber",
      progress,
      daysLeft,
      expiryDate,
      isExpired: false,
      isSoon: true,
      isReference: true,
    };
  }

  return {
    label: "Actif",
    tone: "emerald",
    progress,
    daysLeft,
    expiryDate,
    isExpired: false,
    isSoon: false,
    isReference: true,
  };
}

export function formatDateShort(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}